import React, { Component } from "react";
import { Link, withRouter } from "react-router-dom";
import { connect } from "react-redux";
import axios from "axios";
import { Store } from "react-notifications-component";
import {
  Plus, Eye, Pencil, Trash2, Filter, Star, Zap, ChevronDown, Feather, Download,
} from "lucide-react";

import setAuthToken from "../../utils/setAuthToken";
import confirmAlert from "../../utils/confirmAlert";
import { logoutUser } from "../../actions/authActions";
import { toggleDarkMode } from "../../actions/preferenceActions";
import ArticleQuickEditDrawer from "./quick-edit/ArticleQuickEditDrawer";
import AdminCommandPalette from "./AdminCommandPalette";
import AdminNotifications from "./AdminNotifications";

import {
  AdminAppShell,
  PageHeader,
  DataTable,
  Button,
  IconButton,
  Select,
  FormField,
  SearchInput,
  Stat,
  Tooltip,
  DropdownMenu,
  Breadcrumb,
  ArticleStatusBadge,
  ArticleFeaturedBadge,
  ArticleTypeLabel,
} from "../../design-system";
import { buildAdminBreadcrumb } from "../../utils/adminBreadcrumb";

class ArticlesList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      articles: [],
      loading: true,
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
      itemsPerPage: 20,
      filters: { type: "", status: "" },
      search: "",
      quickEditId: null,
    };
  }

  componentDidMount() {
    this.loadArticles();
  }

  loadArticles = async () => {
    this.setState({ loading: true });
    try {
      const token = localStorage.getItem("jwtToken");
      if (token) setAuthToken(token);

      const { currentPage, itemsPerPage, filters } = this.state;
      const limit = itemsPerPage;
      const offset = (currentPage - 1) * limit;

      const params = new URLSearchParams();
      if (filters.type)   params.append("type",   filters.type);
      if (filters.status) params.append("status", filters.status);
      params.append("limit",  limit);
      params.append("offset", offset);

      const response = await axios.get(`/api/articles?${params.toString()}`);
      const articles = response.data.articles || [];
      const total = response.data.pagination?.total || 0;
      const totalPages = Math.max(1, Math.ceil(total / limit));

      this.setState({ articles, totalItems: total, totalPages, loading: false });
    } catch (error) {
      console.error("Error loading articles:", error);
      this.setState({ loading: false });
      Store.addNotification({
        title: "Erreur",
        message: "Impossible de charger les articles",
        type: "danger", insert: "top", container: "top-right", dismiss: { duration: 3000 },
      });
    }
  };

  handlePageChange = (page) => {
    this.setState({ currentPage: page }, () => {
      this.loadArticles();
      window.scrollTo({ top: 0, behavior: "smooth" });
    });
  };

  handleFilterChange = (filterType, value) => {
    this.setState(
      (prevState) => ({
        filters: { ...prevState.filters, [filterType]: value },
        currentPage: 1,
      }),
      this.loadArticles,
    );
  };

  handleDelete = (article) => {
    confirmAlert({
      title: "Supprimer cet article ?",
      message: `« ${article.title} » sera supprimé définitivement. Cette action est irréversible.`,
      type: "danger",
      confirmText: "Supprimer",
      cancelText: "Annuler",
      onConfirm: async () => {
        try {
          const token = localStorage.getItem("jwtToken");
          if (token) setAuthToken(token);
          await axios.delete(`/api/articles/${article.id}`);
          Store.addNotification({
            title: "Article supprimé",
            message: `« ${article.title} » a été retiré.`,
            type: "success", insert: "top", container: "top-right", dismiss: { duration: 3000 },
          });
          this.loadArticles();
        } catch (error) {
          console.error("Error deleting article:", error);
          Store.addNotification({
            title: "Erreur",
            message: "Impossible de supprimer l'article",
            type: "danger", insert: "top", container: "top-right", dismiss: { duration: 3000 },
          });
        }
      },
    });
  };

  // Patch silencieux d'un article depuis la liste.
  quickPatch = async (article, patch, successMessage) => {
    try {
      const token = localStorage.getItem("jwtToken");
      if (token) setAuthToken(token);
      const res = await axios.put(`/api/articles/${article.id}`, patch);
      const saved = res.data.article || { ...article, ...patch };
      // Patch local pour feedback immédiat
      this.setState((s) => ({
        articles: s.articles.map((a) => (a.id === saved.id ? { ...a, ...saved } : a)),
      }));
      if (successMessage) {
        Store.addNotification({
          title: successMessage,
          type: "success", insert: "top", container: "top-right", dismiss: { duration: 2000 },
        });
      }
    } catch (error) {
      console.error("quickPatch error:", error);
      Store.addNotification({
        title: "Erreur",
        message: error.response?.data?.message || "Impossible d'enregistrer",
        type: "danger", insert: "top", container: "top-right", dismiss: { duration: 3500 },
      });
    }
  };

  handleToggleFeatured = (article) => {
    this.quickPatch(
      article,
      { featured: !article.featured },
      article.featured ? "Retiré de la une" : "Mis à la une",
    );
  };

  handleChangeStatus = (article, status) => {
    if (article.status === status) return;
    const labels = { PUBLISHED: "publié", SCHEDULED: "programmé", DRAFT: "brouillon", ARCHIVED: "archivé" };
    this.quickPatch(article, { status }, `Passé en ${labels[status]}`);
  };

  openQuickEdit = (article) => this.setState({ quickEditId: article.id });
  closeQuickEdit = () => this.setState({ quickEditId: null });

  onQuickEditSaved = (saved) => {
    this.setState((s) => ({
      articles: s.articles.map((a) => (a.id === saved.id ? { ...a, ...saved } : a)),
    }));
  };

  onLogout = () => {
    confirmAlert({
      title: "Déconnexion",
      message: "Êtes-vous sûr de vouloir vous déconnecter ?",
      type: "warning",
      confirmText: "Déconnexion",
      cancelText: "Annuler",
      onConfirm: () => {
        this.props.logoutUser();
        this.props.history.push("/login");
      },
    });
  };

  onToggleDarkMode = () => {
    const { _preferences } = this.props;
    this.props.toggleDarkMode(!_preferences?._dark);
  };

  computeStats() {
    const { articles, totalItems } = this.state;
    return {
      total:     totalItems,
      published: articles.filter((a) => a.status === "PUBLISHED").length,
      scheduled: articles.filter((a) => a.status === "SCHEDULED").length,
      drafts:    articles.filter((a) => a.status === "DRAFT").length,
      featured:  articles.filter((a) => a.featured).length,
    };
  }

  buildColumns() {
    return [
      {
        key: "featured",
        header: "",
        width: 40,
        render: (row) => (
          <span data-no-row-click>
            <Tooltip content={row.featured ? "Retirer de la une" : "Mettre à la une"}>
              <button
                type="button"
                onClick={() => this.handleToggleFeatured(row)}
                aria-label={row.featured ? "Retirer de la une" : "Mettre à la une"}
                className="p-1 rounded hover:bg-bg-2 transition-colors"
              >
                <Star
                  className={row.featured ? "w-4 h-4 text-warn fill-current" : "w-4 h-4 text-mute-2"}
                  strokeWidth={row.featured ? 0 : 1.8}
                />
              </button>
            </Tooltip>
          </span>
        ),
      },
      {
        key: "title",
        header: "Titre",
        sortable: true,
        render: (row) => (
          <div className="leading-tight min-w-0">
            <Link
              to={`/dashboard/articles/${row.id}/view`}
              className="text-[14px] font-semibold text-text hover:underline underline-offset-2"
            >
              {row.title}
            </Link>
            {row.excerpt && (
              <div className="text-[11.5px] text-mute mt-0.5 font-serif italic truncate max-w-md">
                — {row.excerpt}
              </div>
            )}
          </div>
        ),
      },
      {
        key: "type",
        header: "Type",
        width: 160,
        render: (row) => (
          <span className="text-sm text-text-2"><ArticleTypeLabel type={row.type} /></span>
        ),
      },
      {
        key: "status",
        header: "Statut",
        width: 170,
        render: (row) => (
          <span data-no-row-click className="inline-flex flex-col gap-0.5">
            <DropdownMenu
              align="start"
              trigger={
                <button
                  type="button"
                  className="inline-flex items-center gap-1 group"
                  aria-label="Changer le statut"
                >
                  <ArticleStatusBadge status={row.status} />
                  <ChevronDown className="w-3 h-3 text-mute group-hover:text-text transition-colors" strokeWidth={2} />
                </button>
              }
            >
              {({ close }) => (
                <>
                  <DropdownMenu.Label>Statut</DropdownMenu.Label>
                  {["PUBLISHED", "DRAFT", "ARCHIVED"].map((s) => (
                    <DropdownMenu.Item
                      key={s}
                      onSelect={() => { this.handleChangeStatus(row, s); close(); }}
                    >
                      <ArticleStatusBadge status={s} size="sm" />
                      {row.status === s && (
                        <span className="ml-auto text-[10.5px] font-mono uppercase tracking-wider text-mute">
                          actuel
                        </span>
                      )}
                    </DropdownMenu.Item>
                  ))}
                  {row.status === "SCHEDULED" && (
                    <>
                      <DropdownMenu.Separator />
                      <DropdownMenu.Label>Reprogrammer dans l'éditeur</DropdownMenu.Label>
                    </>
                  )}
                </>
              )}
            </DropdownMenu>
            {row.status === "SCHEDULED" && row.publishedAt && (
              <span className="font-mono text-[10.5px] text-mute">
                {new Date(row.publishedAt).toLocaleDateString("fr-FR", {
                  day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
                })}
              </span>
            )}
          </span>
        ),
      },
      {
        key: "author",
        header: "Auteur",
        width: 160,
        render: (row) => (
          <span className="text-sm text-text-2">{row.author?.name || row.author?.email || "—"}</span>
        ),
      },
      {
        key: "publishedAt",
        header: "Publié le",
        sortable: true,
        width: 130,
        render: (row) =>
          row.publishedAt
            ? <span className="font-mono text-xs text-mute">{new Date(row.publishedAt).toLocaleDateString("fr-FR")}</span>
            : <span className="text-mute-2">—</span>,
      },
      {
        key: "actions",
        header: "",
        width: 160,
        render: (row) => (
          <div className="flex items-center gap-0.5 justify-end" data-no-row-click>
            <Tooltip content="Édition rapide">
              <IconButton
                size="sm"
                variant="ghost"
                icon={<Zap strokeWidth={1.8} />}
                aria-label={`Édition rapide ${row.title}`}
                onClick={() => this.openQuickEdit(row)}
              />
            </Tooltip>
            <Tooltip content="Voir">
              <IconButton
                as={Link}
                to={`/dashboard/articles/${row.id}/view`}
                size="sm"
                variant="ghost"
                icon={<Eye strokeWidth={1.8} />}
                aria-label={`Voir ${row.title}`}
              />
            </Tooltip>
            <Tooltip content="Éditer en détail">
              <IconButton
                as={Link}
                to={`/dashboard/articles/${row.id}`}
                size="sm"
                variant="ghost"
                icon={<Pencil strokeWidth={1.8} />}
                aria-label={`Modifier ${row.title}`}
              />
            </Tooltip>
            <Tooltip content="Supprimer">
              <IconButton
                size="sm"
                variant="ghost"
                icon={<Trash2 strokeWidth={1.8} />}
                aria-label={`Supprimer ${row.title}`}
                onClick={() => this.handleDelete(row)}
              />
            </Tooltip>
          </div>
        ),
      },
    ];
  }

  render() {
    const { articles, loading, currentPage, itemsPerPage, totalItems, filters, search, quickEditId } = this.state;
    const { user, _preferences, location } = this.props;
    const stats = this.computeStats();

    return (
      <AdminAppShell
        user={user}
        currentPath={location.pathname}
        isDarkMode={_preferences?._dark || false}
        onToggleDarkMode={this.onToggleDarkMode}
        onLogout={this.onLogout}
        LinkComponent={Link}
        pageTitle="Articles"
        breadcrumb={<Breadcrumb items={buildAdminBreadcrumb(location.pathname)} LinkComponent={Link} />}
        counts={{ articles: totalItems }}
        topbarCenter={<AdminCommandPalette />}
        topbarRight={<AdminNotifications />}
      >
        <div className="w-full px-5 sm:px-6 lg:px-8 xl:px-10 py-6 lg:py-8 space-y-6 lg:space-y-7">
          <PageHeader
            size="hero"
            eyebrow="— Section ii · Articles"
            title={<>Écrire, <span className="font-serif italic font-normal align-baseline">recommencer.</span></>}
            description={`${stats.total} entrées, ${stats.drafts} brouillons à finaliser, un index de lecture qui ne cesse de monter. Bienvenue dans l'arrière-cuisine.`}
            actions={
              <>
                <Button
                  as={Link}
                  to="/dashboard/articles/new"
                  leftIcon={<Feather className="w-4 h-4" strokeWidth={1.8} />}
                >
                  Nouvel article
                </Button>
                <Button
                  variant="secondary"
                  leftIcon={<Download className="w-4 h-4" strokeWidth={1.8} />}
                >
                  Exporter (.md)
                </Button>
              </>
            }
          />

          <Stat.Group>
            <Stat variant="ink"   label="Total"      value={String(stats.total)}     hint="tous statuts" />
            <Stat variant="cream" label="Publiés"    value={String(stats.published)} hint="page courante" />
            <Stat variant="cream" label="Brouillons" value={String(stats.drafts)}    hint="à finaliser" />
            <Stat variant="ink"   label="À la une"   value={String(stats.featured)}  hint="mis en avant"
              icon={<Star className="w-4 h-4" strokeWidth={1.8} />} />
          </Stat.Group>

          <div className="flex items-end gap-3 mb-4 flex-wrap">
            <FormField label="Type" className="min-w-[220px]">
              <Select
                value={filters.type}
                onChange={(e) => this.handleFilterChange("type", e.target.value)}
                placeholder="Tous les types"
                options={[
                  { value: "VEILLE_TECHNOLOGIQUE", label: "Veille technologique" },
                  { value: "VEILLE_JURIDIQUE",     label: "Veille juridique" },
                  { value: "STAGE",                label: "Stage" },
                  { value: "PPE",                  label: "PPE" },
                  { value: "PP",                   label: "PP" },
                  { value: "OTHER",                label: "Autre" },
                ]}
              />
            </FormField>
            <FormField label="Statut" className="min-w-[200px]">
              <Select
                value={filters.status}
                onChange={(e) => this.handleFilterChange("status", e.target.value)}
                placeholder="Tous les statuts"
                options={[
                  { value: "PUBLISHED", label: "Publié" },
                  { value: "SCHEDULED", label: "Programmé" },
                  { value: "DRAFT",     label: "Brouillon" },
                  { value: "ARCHIVED",  label: "Archivé" },
                ]}
              />
            </FormField>
            {(filters.type || filters.status) && (
              <Button
                size="sm"
                variant="ghost"
                leftIcon={<Filter className="w-3.5 h-3.5" strokeWidth={1.8} />}
                onClick={() => this.setState({ filters: { type: "", status: "" }, currentPage: 1 }, this.loadArticles)}
              >
                Réinitialiser
              </Button>
            )}
            <div className="ml-auto text-xs text-mute">
              Astuce : clique sur l'<Zap className="w-3 h-3 inline -mt-0.5" strokeWidth={1.8} /> pour l'édition rapide.
            </div>
          </div>

          <DataTable
            data={articles}
            columns={this.buildColumns()}
            rowKey="id"
            isLoading={loading}
            variant="duotone"
            showRowIndex
            rowIndexStart={(currentPage - 1) * itemsPerPage + 1}
            page={currentPage}
            pageSize={itemsPerPage}
            totalItems={totalItems}
            onPageChange={this.handlePageChange}
          />

          <ArticleQuickEditDrawer
            isOpen={!!quickEditId}
            onClose={this.closeQuickEdit}
            articleId={quickEditId}
            onSaved={this.onQuickEditSaved}
          />
        </div>
      </AdminAppShell>
    );
  }
}

const mapStateToProps = (state) => ({
  user: state.auth.user,
  _preferences: state._preferences,
});

export default connect(mapStateToProps, { logoutUser, toggleDarkMode })(withRouter(ArticlesList));
